import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { applyConsent, getConsent, onOpenCookieSettings, saveConsent, trackPageView } from '../lib/consent';
import { useTranslation } from '../hooks/useTranslation';

const Toggle = ({ id, label, description, checked, onChange }) => (
  <label htmlFor={id} className="flex items-start gap-3 py-3 border-t border-slate-800 cursor-pointer">
    <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-1 h-4 w-4 accent-cyan-500" />
    <span>
      <span className="block text-sm font-semibold text-slate-100">{label}</span>
      <span className="block text-xs text-slate-400 leading-relaxed">{description}</span>
    </span>
  </label>
);

const CookieBanner = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [customising, setCustomising] = useState(false);
  const [choices, setChoices] = useState({ analytics: false, marketing: false });
  const firstPath = useRef(true);

  useEffect(() => {
    const consent = getConsent();
    applyConsent(consent);
    if (!consent) setOpen(true);
    return onOpenCookieSettings(() => {
      const current = getConsent();
      setChoices({ analytics: Boolean(current && current.analytics), marketing: Boolean(current && current.marketing) });
      setCustomising(Boolean(current));
      setOpen(true);
    });
  }, []);

  useEffect(() => {
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    trackPageView();
  }, [pathname]);

  if (!open) return null;

  const choose = (value) => {
    saveConsent(value);
    setOpen(false);
    setCustomising(false);
  };

  const secondary = 'h-10 bg-slate-100 hover:bg-white text-slate-950 text-sm font-semibold transition-colors';
  const primary = 'h-10 bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-semibold transition-colors';

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('cookies.title')}
      className="fixed z-[60] bottom-4 left-4 right-4 sm:right-auto sm:max-w-md border border-slate-700 bg-slate-900 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]"
    >
      <div className="h-[3px] w-full bg-cyan-500" />
      <div className="p-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-cyan-400 mb-2">{t('cookies.title')}</p>
        <p className="text-sm text-slate-300 leading-relaxed mb-4">
          {t('cookies.text')}{' '}
          <Link to="/cookie-policy" className="underline underline-offset-2 text-slate-100 hover:text-cyan-400">
            {t('cookies.policyLink')}
          </Link>
          .
        </p>

        {customising && (
          <div className="mb-4 border-b border-slate-800">
            <Toggle
              id="consent-analytics"
              label={t('cookies.analyticsLabel')}
              description={t('cookies.analyticsText')}
              checked={choices.analytics}
              onChange={(analytics) => setChoices((c) => ({ ...c, analytics }))}
            />
            <Toggle
              id="consent-marketing"
              label={t('cookies.marketingLabel')}
              description={t('cookies.marketingText')}
              checked={choices.marketing}
              onChange={(marketing) => setChoices((c) => ({ ...c, marketing }))}
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={() => choose({ analytics: false, marketing: false })} className={secondary}>
            {t('cookies.reject')}
          </button>
          <button type="button" onClick={() => choose({ analytics: true, marketing: true })} className={primary}>
            {t('cookies.accept')}
          </button>
        </div>
        <button
          type="button"
          onClick={() => (customising ? choose(choices) : setCustomising(true))}
          className="mt-3 w-full text-xs text-slate-400 hover:text-slate-100 underline underline-offset-2"
        >
          {customising ? t('cookies.save') : t('cookies.customise')}
        </button>
      </div>
    </div>
  );
};

export default CookieBanner;
